import React, { useState, useEffect } from 'react';
import { Modal, Table, Button, Input, Tag, Empty, Select, message } from 'antd';
import {
  SearchOutlined,
  DownloadOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  FilterOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
} from '@ant-design/icons';
import {
  getReversepaylist,
  getReversepaylistexcel,
  getReversalPoolAssignments,
} from '../../api/login/Login';
import * as XLSX from 'xlsx';

function ReversalHistoryModal({
  open,
  onClose,
  walletType,
  walletTitle,
  totalBalance,
  gradient,
  badgeBg,
  badgeColor,
  icon,
}) {
  // Direction toggle: 'incoming' (default) vs 'outgoing'
  const [direction, setDirection] = useState('incoming');

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalSum, setTotalSum] = useState(0);

  const [activeWallet, setActiveWallet] = useState(walletType || 'Main Wallet');
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [searchText, setSearchText] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  // Fetch incoming reversals (credits into the pool)
  const fetchIncomingHistory = async (pageNumber = 1, search = '', wType = activeWallet) => {
    setLoading(true);
    try {
      const params = {
        page: pageNumber - 1,
        count: pageSize,
      };

      if (wType && wType !== 'all') {
        params.wallet = wType;
      }

      if (search.trim()) {
        params.username = search.trim();
      }

      const response = await getReversepaylist({ params });

      if (
        response?.data?.statusCode === 200 ||
        response?.data?.status_Code === 200 ||
        response?.status === 200 ||
        response?.statusCode === 200
      ) {
        const payload = response?.data?.data || response?.data || {};
        const list = payload.requestList || [];
        setData(list);
        setTotalCount(payload.totalCount || 0);

        if (payload.totalAmount !== undefined) {
          setTotalSum(Number(payload.totalAmount));
        } else {
          const sum = list.reduce((acc, curr) => acc + Number(curr.amount || 0), 0);
          setTotalSum(Number(sum.toFixed(2)));
        }
        setPage(pageNumber);
      } else {
        setData([]);
        setTotalCount(0);
        setTotalSum(0);
      }
    } catch (error) {
      console.error('Failed to fetch incoming reversal history:', error);
      message.error(error?.message || 'Failed to load incoming reversal history');
    } finally {
      setLoading(false);
    }
  };

  // Fetch outgoing assignments (funds disbursed from pool to users)
  const fetchOutgoingHistory = async (pageNumber = 1, search = '', wType = activeWallet) => {
    setLoading(true);
    try {
      const params = {
        page: pageNumber - 1,
        count: pageSize,
      };

      if (wType && wType !== 'all') {
        params.pool = wType;
      }

      if (search.trim()) {
        params.search = search.trim();
      }

      const response = await getReversalPoolAssignments(params);

      if (
        response?.statusCode === 200 ||
        response?.status_Code === 200 ||
        response?.status === 200 ||
        response?.data?.statusCode === 200
      ) {
        const payload = response?.data || response || {};
        const list = Array.isArray(payload.list) ? payload.list : [];
        setData(list);
        setTotalCount(payload.total || list.length);
        setTotalSum(Number(payload.totalAmount || 0));
        setPage(pageNumber);
      } else {
        setData([]);
        setTotalCount(0);
        setTotalSum(0);
      }
    } catch (error) {
      console.error('Failed to fetch outgoing assignments:', error);
      message.error(error?.message || 'Failed to load outgoing assignments');
    } finally {
      setLoading(false);
    }
  };

  // Unified fetch based on current direction
  const fetchHistory = (pageNumber = 1, search = searchText, wType = activeWallet, dir = direction) => {
    if (dir === 'outgoing') {
      fetchOutgoingHistory(pageNumber, search, wType);
    } else {
      fetchIncomingHistory(pageNumber, search, wType);
    }
  };

  // On open, always default to incoming
  useEffect(() => {
    if (open) {
      const initialWallet = walletType || 'Main Wallet';
      setActiveWallet(initialWallet);
      setDirection('incoming');
      setSearchText('');
      setPage(1);
      fetchIncomingHistory(1, '', initialWallet);
    }
  }, [open, walletType]);

  const handleDirectionChange = (newDir) => {
    setDirection(newDir);
    setSearchText('');
    setPage(1);
    fetchHistory(1, '', activeWallet, newDir);
  };

  const handleWalletChange = (val) => {
    setActiveWallet(val);
    setPage(1);
    fetchHistory(1, searchText, val, direction);
  };

  const handleSearch = () => {
    setPage(1);
    fetchHistory(1, searchText, activeWallet, direction);
  };

  const handleReset = () => {
    setSearchText('');
    setPage(1);
    fetchHistory(1, '', activeWallet, direction);
  };

  // Export Excel: exports ONLY incoming records when on incoming, and ONLY outgoing records when on outgoing
  const handleExportExcel = async () => {
    try {
      setExportLoading(true);

      if (direction === 'incoming') {
        // Export incoming reversals
        const params = {
          page: 0,
          count: 2000,
        };
        if (activeWallet && activeWallet !== 'all') {
          params.wallet = activeWallet;
        }
        if (searchText.trim()) {
          params.username = searchText.trim();
        }
        const response = await getReversepaylistexcel({ params });
        const excelData = response?.data?.data?.requestList || data;

        if (!excelData || excelData.length === 0) {
          message.warning('No incoming records to export');
          return;
        }

        const formatted = excelData.map((item, idx) => ({
          'S.No': idx + 1,
          'Date & Time': item.createdAt || 'N/A',
          'Transaction ID': item.txn_id || 'N/A',
          'Reversal Pool': item.wallet || activeWallet,
          'Username': item.username || 'N/A',
          'Member Code': item.member_code || 'N/A',
          'User Type': item.user_type || 'Retailer',
          'Reversed Amount (INR)': Number(item.amount || 0).toFixed(2),
          'Opening Balance': Number(item.o_bal || 0).toFixed(2),
          'Closing Balance': Number(item.c_bal || 0).toFixed(2),
          'Reversed By Staff': item.staffName || 'Admin',
          'Remark': item.remark || '',
        }));

        const ws = XLSX.utils.json_to_sheet(formatted);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Incoming_Reversals');
        const filename = `${String(activeWallet).replace(/\s+/g, '_')}_Incoming_Reversals_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, filename);
        message.success('Incoming reversals Excel exported successfully');
      } else {
        // Export outgoing assignments ONLY
        const params = {
          page: 0,
          count: 2000,
        };
        if (activeWallet && activeWallet !== 'all') {
          params.pool = activeWallet;
        }
        if (searchText.trim()) {
          params.search = searchText.trim();
        }
        const response = await getReversalPoolAssignments(params);
        const payload = response?.data || response || {};
        const excelData = Array.isArray(payload.list) ? payload.list : data;

        if (!excelData || excelData.length === 0) {
          message.warning('No outgoing assignment records to export');
          return;
        }

        const formatted = excelData.map((item, idx) => ({
          'S.No': idx + 1,
          'Date & Time': item.createdAt || 'N/A',
          'Transaction ID': item.txn_id || 'N/A',
          'Source Reversal Pool': item.pool || activeWallet,
          'Recipient Name': item.user_id?.name || 'N/A',
          'Member Code': item.user_id?.refer_id || 'N/A',
          'Mobile': item.user_id?.mobile || 'N/A',
          'Role': item.user_id?.user_type_id?.user_type || 'Retailer',
          'Assigned Amount (INR)': Number(item.amount || 0).toFixed(2),
          'Pool Opening Balance': Number(item.pool_o_bal || 0).toFixed(2),
          'Pool Closing Balance': Number(item.pool_c_bal || 0).toFixed(2),
          'User Opening Balance': Number(item.user_o_bal || 0).toFixed(2),
          'User Closing Balance': Number(item.user_c_bal || 0).toFixed(2),
          'Assigned By Staff': item.staff_id?.name || 'Admin',
          'Remark': item.remark || '',
        }));

        const ws = XLSX.utils.json_to_sheet(formatted);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Outgoing_Assignments');
        const filename = `${String(activeWallet).replace(/\s+/g, '_')}_Outgoing_Assignments_${new Date()
          .toISOString()
          .slice(0, 10)}.xlsx`;
        XLSX.writeFile(wb, filename);
        message.success('Outgoing assignments Excel exported successfully');
      }
    } catch (err) {
      console.error(err);
      message.error('Failed to export Excel');
    } finally {
      setExportLoading(false);
    }
  };

  const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Columns for Incoming Reversals (Credits into pool)
  const incomingColumns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 55,
      align: 'center',
      render: (text, record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (text) => (
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
          {text || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Wallet Pool',
      dataIndex: 'wallet',
      key: 'wallet',
      width: 140,
      render: (wVal) => {
        const val = wVal || activeWallet || 'Main Wallet';
        const isMain = /main/i.test(val);
        const isAeps = /aeps/i.test(val);
        const isCashback = /cashback/i.test(val);
        return (
          <Tag
            color={isMain ? 'green' : isAeps ? 'blue' : isCashback ? 'purple' : 'default'}
            style={{ fontWeight: 700, borderRadius: 6, padding: '2px 8px' }}
          >
            {isMain ? 'Main Wallet' : isAeps ? 'AEPS Wallet' : isCashback ? 'Cashback' : val}
          </Tag>
        );
      },
    },
    {
      title: 'Transaction ID',
      dataIndex: 'txn_id',
      key: 'txn_id',
      width: 165,
      render: (text) => (
        <span
          style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#0369a1', fontWeight: 600 }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Reversed From (User)',
      key: 'user',
      width: 190,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem' }}>
            {record.username || 'Unknown'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
              {record.member_code || '-'}
            </span>
            {record.user_type && (
              <Tag color="blue" style={{ fontSize: '0.7rem', padding: '0 6px', margin: 0 }}>
                {record.user_type}
              </Tag>
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'Reversed Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (amount) => (
        <div style={{ textAlign: 'right' }}>
          <span style={{ color: '#16a34a', fontWeight: 800, fontSize: '0.95rem' }}>
            +₹{formatCurrency(amount)}
          </span>
        </div>
      ),
    },
    {
      title: 'User Balance Effect',
      key: 'balances',
      width: 160,
      render: (text, record) => (
        <div style={{ fontSize: '0.78rem' }}>
          <div style={{ color: '#64748b' }}>
            Opening: <span style={{ color: '#334155', fontWeight: 600 }}>₹{Number(record.o_bal || 0).toFixed(2)}</span>
          </div>
          <div style={{ color: '#dc2626', fontWeight: 600 }}>
            Closing: ₹{Number(record.c_bal || 0).toFixed(2)}
          </div>
        </div>
      ),
    },
    {
      title: 'Staff / Remark',
      key: 'staff',
      width: 175,
      render: (text, record) => (
        <div style={{ fontSize: '0.8rem' }}>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>👤 {record.staffName || 'Admin'}</div>
          <div style={{ color: '#64748b', fontStyle: 'italic', marginTop: 2 }}>
            {record.remark || 'Amount reversed by admin'}
          </div>
        </div>
      ),
    },
  ];

  // Columns for Outgoing Assignments (Debits disbursed to members)
  const outgoingColumns = [
    {
      title: 'S.No',
      key: 'sno',
      width: 55,
      align: 'center',
      render: (text, record, index) => (page - 1) * pageSize + index + 1,
    },
    {
      title: 'Date & Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 155,
      render: (text) => (
        <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#334155' }}>
          {text ? new Date(text).toLocaleString('en-IN') : 'N/A'}
        </span>
      ),
    },
    {
      title: 'Reversal Pool',
      dataIndex: 'pool',
      key: 'pool',
      width: 135,
      render: (poolVal) => {
        const val = poolVal || activeWallet || 'Main Wallet';
        const isMain = /main/i.test(val);
        const isAeps = /aeps/i.test(val);
        const isCashback = /cashback/i.test(val);
        return (
          <Tag
            color={isMain ? 'green' : isAeps ? 'blue' : isCashback ? 'purple' : 'default'}
            style={{ fontWeight: 700, borderRadius: 6, padding: '2px 8px' }}
          >
            {val}
          </Tag>
        );
      },
    },
    {
      title: 'Transaction ID',
      dataIndex: 'txn_id',
      key: 'txn_id',
      width: 170,
      render: (text) => (
        <span
          style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#0369a1', fontWeight: 600 }}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Recipient Member',
      key: 'recipient',
      width: 200,
      render: (text, record) => {
        const u = record.user_id || {};
        const role = u.user_type_id?.user_type || 'Retailer';
        return (
          <div>
            <div style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem' }}>
              {u.name || 'Unknown Member'}
            </div>
            <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 2 }}>
              <span style={{ color: '#0284c7', fontWeight: 600 }}>{u.refer_id || 'N/A'}</span> • {u.mobile}
            </div>
            <Tag color="blue" style={{ fontSize: '0.68rem', padding: '0 6px', marginTop: 3 }}>
              {role}
            </Tag>
          </div>
        );
      },
    },
    {
      title: 'Assigned Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 140,
      align: 'right',
      render: (amount) => (
        <div style={{ textAlign: 'right' }}>
          <span style={{ color: '#dc2626', fontWeight: 800, fontSize: '0.98rem' }}>
            -₹{formatCurrency(amount)}
          </span>
          <div style={{ fontSize: '0.7rem', color: '#ef4444', fontWeight: 600 }}>Disbursed</div>
        </div>
      ),
    },
    {
      title: 'Pool Balance Effect',
      key: 'poolBalance',
      width: 165,
      render: (text, record) => (
        <div style={{ fontSize: '0.78rem' }}>
          <div style={{ color: '#64748b' }}>
            Opening: <span style={{ color: '#334155', fontWeight: 600 }}>₹{formatCurrency(record.pool_o_bal)}</span>
          </div>
          <div style={{ color: '#059669', fontWeight: 700 }}>
            Closing: ₹{formatCurrency(record.pool_c_bal)}
          </div>
        </div>
      ),
    },
    {
      title: 'User Balance Effect',
      key: 'userBalance',
      width: 165,
      render: (text, record) => (
        <div style={{ fontSize: '0.78rem' }}>
          <div style={{ color: '#64748b' }}>
            Opening: <span style={{ color: '#334155', fontWeight: 600 }}>₹{formatCurrency(record.user_o_bal)}</span>
          </div>
          <div style={{ color: '#16a34a', fontWeight: 700 }}>
            Closing: ₹{formatCurrency(record.user_c_bal)}
          </div>
        </div>
      ),
    },
    {
      title: 'Assigned By / Remark',
      key: 'staff',
      width: 185,
      render: (text, record) => (
        <div style={{ fontSize: '0.8rem' }}>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>
            👤 {record.staff_id?.name || 'Admin'}
          </div>
          <div style={{ color: '#64748b', fontStyle: 'italic', marginTop: 2 }}>
            {record.remark || 'Fund assigned from reversal pool'}
          </div>
        </div>
      ),
    },
  ];

  // Match indicator for incoming
  const isSelectedPoolExact = activeWallet === walletType;
  const isMatch =
    direction === 'incoming' &&
    isSelectedPoolExact &&
    Math.abs(Number(totalSum) - Number(totalBalance)) < 0.01;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={[
        <div
          key="footer"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}
        >
          <div style={{ fontSize: '0.85rem', color: '#475569', fontWeight: 600 }}>
            {direction === 'incoming' ? 'Total Incoming Reversals' : 'Total Assigned to Members'} ({totalCount} txns):{' '}
            <span
              style={{
                color: direction === 'incoming' ? '#16a34a' : '#dc2626',
                fontWeight: 800,
                fontSize: '1rem',
              }}
            >
              {direction === 'incoming' ? '+' : '-'}₹{formatCurrency(totalSum)}
            </span>
          </div>
          <Button type="primary" onClick={onClose}>
            Close
          </Button>
        </div>,
      ]}
      width={1160}
      style={{ top: 20 }}
      title={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingRight: 24,
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: 10,
                background: gradient || 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 10px rgba(0,0,0,0.12)',
              }}
            >
              {icon}
            </div>
            <div>
              <div
                style={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: '#0f172a',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                <span>{walletTitle} - Reversal Audit History</span>
              </div>
              <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                {direction === 'incoming'
                  ? `Showing incoming reversal credits into ${activeWallet}`
                  : `Showing outgoing fund assignments disbursed from ${activeWallet}`}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {isMatch && (
              <div
                style={{
                  background: '#dcfce7',
                  border: '1px solid #86efac',
                  color: '#15803d',
                  borderRadius: 20,
                  padding: '4px 12px',
                  fontSize: '0.78rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 5,
                }}
              >
                <CheckCircleOutlined /> Sum Matches Pool Balance
              </div>
            )}

            <div style={{ textAlign: 'right' }}>
              <div
                style={{
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  color: '#64748b',
                  fontWeight: 700,
                }}
              >
                {direction === 'incoming' ? (isSelectedPoolExact ? 'Pool Balance' : 'Incoming Total') : 'Total Assigned'}
              </div>
              <div
                style={{
                  fontSize: '1.3rem',
                  fontWeight: 800,
                  color: direction === 'incoming' ? '#059669' : '#dc2626',
                  lineHeight: 1.2,
                }}
              >
                ₹{formatCurrency(direction === 'incoming' ? (isSelectedPoolExact ? totalBalance : totalSum) : totalSum)}
              </div>
            </div>
          </div>
        </div>
      }
    >
      {/* Direction Switcher & Toolbar */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          background: '#f8fafc',
          borderRadius: 10,
          marginBottom: 16,
          flexWrap: 'wrap',
          border: '1px solid #e2e8f0',
        }}
      >
        {/* Left: Direction Toggle Pills (Incoming vs Outgoing) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div
            style={{
              display: 'flex',
              background: '#e2e8f0',
              padding: 3,
              borderRadius: 8,
              gap: 4,
            }}
          >
            <button
              type="button"
              onClick={() => handleDirectionChange('incoming')}
              style={{
                border: 'none',
                background: direction === 'incoming' ? '#ffffff' : 'transparent',
                color: direction === 'incoming' ? '#047857' : '#64748b',
                fontWeight: direction === 'incoming' ? 800 : 600,
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                boxShadow: direction === 'incoming' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.82rem',
                transition: 'all 0.15s ease',
              }}
            >
              <ArrowDownOutlined style={{ color: '#10b981' }} /> Incoming Credits
            </button>

            <button
              type="button"
              onClick={() => handleDirectionChange('outgoing')}
              style={{
                border: 'none',
                background: direction === 'outgoing' ? '#ffffff' : 'transparent',
                color: direction === 'outgoing' ? '#dc2626' : '#64748b',
                fontWeight: direction === 'outgoing' ? 800 : 600,
                padding: '6px 14px',
                borderRadius: 6,
                cursor: 'pointer',
                boxShadow: direction === 'outgoing' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: '0.82rem',
                transition: 'all 0.15s ease',
              }}
            >
              <ArrowUpOutlined style={{ color: '#ef4444' }} /> Outgoing Assigned (Debits)
            </button>
          </div>

          {/* Filter Pool */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                fontSize: '0.82rem',
                fontWeight: 700,
                color: '#475569',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <FilterOutlined style={{ color: '#0284c7' }} /> Pool:
            </span>
            <Select
              value={activeWallet}
              onChange={handleWalletChange}
              style={{ width: 175 }}
              size="middle"
            >
              <Select.Option value="Main Wallet">Main Wallet</Select.Option>
              <Select.Option value="Aeps Wallet">AEPS Wallet</Select.Option>
              <Select.Option value="Cashback Wallet">Cashback Wallet</Select.Option>
              <Select.Option value="all">All Pools Combined</Select.Option>
            </Select>
          </div>

          {/* Search Input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 210 }}>
            <Input
              placeholder={
                direction === 'incoming'
                  ? 'Search retailer name / code...'
                  : 'Search txn ID or remark...'
              }
              prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
              style={{ borderRadius: 6 }}
            />
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
            {searchText && <Button onClick={handleReset}>Reset</Button>}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Button
            icon={<ReloadOutlined spin={loading} />}
            onClick={() => fetchHistory(page, searchText, activeWallet, direction)}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            icon={<DownloadOutlined />}
            onClick={handleExportExcel}
            loading={exportLoading}
            style={{
              borderColor: direction === 'incoming' ? '#10b981' : '#f87171',
              color: direction === 'incoming' ? '#059669' : '#dc2626',
              fontWeight: 600,
            }}
          >
            Export {direction === 'incoming' ? 'Incoming' : 'Outgoing'} Excel
          </Button>
        </div>
      </div>

      {/* Table Display */}
      <Table
        dataSource={data}
        columns={direction === 'incoming' ? incomingColumns : outgoingColumns}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total: totalCount,
          onChange: (p) => {
            setPage(p);
            fetchHistory(p, searchText, activeWallet, direction);
          },
          showTotal: (total, range) => (
            <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
              Showing {range[0]}-{range[1]} of {total} {direction === 'incoming' ? 'reversal' : 'assignment'} transactions
            </span>
          ),
        }}
        size="middle"
        scroll={{ x: 1050 }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <span>
                  No {direction === 'incoming' ? 'incoming reversal' : 'outgoing assignment'} transactions found for{' '}
                  <strong>{activeWallet}</strong>
                </span>
              }
            />
          ),
        }}
      />
    </Modal>
  );
}

export default ReversalHistoryModal;
